import { useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import { groupByKey, calculateTextingStatus } from '../../utils/texting';
import _ from 'lodash';
import {
  useTableManageColumns,
  useTableFilterSortSearchManager,
  useTableFilterSortData,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { TextingIndicator } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { buildHeaders, parsePriorResidents } from './PriorResidentsTable/utils';
import { PROPERTY_PATH_MAP } from './constantsV2';
import { useFetchConversations } from '../../hooks/data-fetching/useFetchConversations';
import useHasPermission from '../../hooks/useHasPermission';
import { getUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import PriorResidentsService from '../../services/priorResidentsService';
import { exportAndDownloadCSV } from '../../utils/csv-helpers';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import { TAB_KEYS } from '../ResidentProfile/constants';
import useAppFlags from 'utils/featureFlags';
import { adaptHeaders } from 'utils/table-helpers';

const NAME = 'manage-prior-residents';

export const useManagePriorResidents = ({
  intl,
  selectedProperty,
  userId,
  locale,
  organizationId,
  propertyId,
  isAllCommercial,
  onPageChange,
  currentPage,
  perPage,
}) => {
  const name = useUniqueTableName(NAME);
  const hasCommunicationCreatePermission = useHasPermission(
    'communication-create',
  );
  const showTextingColumn =
    selectedProperty.isTwoWayCommunicationActive &&
    hasCommunicationCreatePermission;
  const flags = useAppFlags();
  const showCollectionsColumns = flags?.collectionsOneWay;
  const showFloorPlanColumn = flags?.floorPlanChanges;
  const headers = useMemo(() => {
    const headers = buildHeaders({
      intl,
      isAllCommercial,
      showTextingColumn,
      showCollectionsColumns,
      showFloorPlanColumn,
    });

    adaptHeaders(headers, { flags });

    return headers;
  }, [
    intl,
    isAllCommercial,
    showTextingColumn,
    showCollectionsColumns,
    showFloorPlanColumn,
    flags.leslie,
  ]);

  const {
    order,
    orderBy,
    handleSortChange,
    filterState,
    filterTypeState,
    dateState,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    initialOrderBy: 'moveOutDate',
    initialOrder: 'DESC',
    headers,
  });
  const { results, paginationMeta, isLoading, isSuccess } =
    useFetchPriorResidents({
      organizationId,
      propertyId,
      isAllCommercial,
      onPageChange,
      currentPage,
      perPage,
      searchState,
      enabled: true,
    });

  const parsedResults = useParsePriorResidentResults(results, isAllCommercial);

  const twoWayRecipientIds = useMemo(
    () => parsedResults.map((r) => r.twoWayRecipientIds).flat(),
    [parsedResults],
  );
  const [conversations] = useFetchConversations(
    twoWayRecipientIds,
    showTextingColumn,
  );
  const conversationsDictionary = useMemo(() => {
    return groupByKey(conversations, (c) => c.recipient.customerId);
  }, [conversations]);
  const resultsWithConvo = useMemo(
    () =>
      parsedResults.map((resident) => {
        resident.texting = calculateTextingStatus(
          resident.twoWayRecipientIds,
          conversationsDictionary,
        );

        return resident;
      }),
    [parsedResults, conversationsDictionary],
  );

  const sortedAndFilteredResults = useTableFilterSortData({
    results: resultsWithConvo,
    order,
    orderBy,
    PROPERTY_PATH_MAP,
    searchState,
    filterState,
    filterTypeState,
    dateState,
  });

  const rows = useBuildPriorResidentResults(
    sortedAndFilteredResults,
    isAllCommercial,
    resultsWithConvo,
  );

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
  });

  const { handleCSVButtonClick } = useCSVExport({
    hasAnyFilters:
      Object.keys(filterState)?.length ||
      Object.keys(dateState)?.length ||
      Object.keys(searchState)?.length,
    filteredHeaders,
    rows,
    enabled: flags?.exportPriorResidentScreen,
  });

  return {
    filterState,
    filterTypeState,
    count: rows?.length ?? 0,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    name,
    searchState,
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    isLoading,
    isSuccess,
    totalCount: resultsWithConvo?.length ?? 0,
    rows,
    handleCSVButtonClick,
    ...paginationMeta,
  };
};

export const useFetchPriorResidents = ({
  organizationId,
  propertyId,
  enabled,
  isAllCommercial,
  onPageChange,
  currentPage,
  perPage,
  searchState,
}) => {
  const queryKey = [
    NAME,
    organizationId,
    propertyId,
    enabled,
    onPageChange,
    currentPage,
    perPage,
  ];
  let { data, isLoading, isSuccess } = useQuery(
    queryKey,
    () => {
      return new PriorResidentsService().getAllPriorResidents(
        currentPage,
        3000,
        {},
        '',
        organizationId,
        propertyId,
        true,
      );
    },
    {
      enabled: Boolean(enabled && organizationId && propertyId),
      refetchOnWindowFocus: false,
    },
  );
  const results = useMemo(() => data?.results ?? [], [data?.results]);
  const paginationMeta = useMemo(() => data?.meta ?? {}, [data?.meta]);
  return {
    results,
    paginationMeta,
    isLoading,
    isSuccess,
  };
};

export const useParsePriorResidentResults = (
  results = [],
  isCommercial = false,
) => {
  return useMemo(() => {
    return results?.map((result) => ({
      ...result,
      balance: +result.balance,
      subsidyBalance: +result.subsidyBalance,
      priorTenants: parsePriorResidents(result, isCommercial),
      priorResidents: parsePriorResidents(result, isCommercial),
    }));
  }, [results, isCommercial]);
};

export const useBuildPriorResidentResults = (
  results = [],
  isCommercial = false,
) => {
  return useMemo(() => {
    return results?.map((result) => ({
      priorTenants: {
        variant: 'link',
        to: getUrlWithSelectedPropertyId(
          `/prior-resident/${result.residentId}`,
        ),
        value: parsePriorResidents(result, isCommercial),
      },
      priorResidents: {
        variant: 'link',
        to: getUrlWithSelectedPropertyId(
          `/prior-resident/${result.residentId}`,
        ),
        value: parsePriorResidents(result, isCommercial),
      },
      texting: {
        variant: result.texting.TOTAL > 0 ? 'link' : undefined,
        value: <TextingIndicator {...result.texting} />,
        to:
          result.texting.TOTAL > 0
            ? getUrlWithSelectedPropertyId(
                `/resident/${result.residentId}?tab=${TAB_KEYS.TEXTING}`,
              )
            : undefined,
      },
      unit: {
        variant: 'link',
        to: getUrlWithSelectedPropertyId(`/unit/${result.unitId}`),
        value: result.unit,
        disabled: result.isUnitSoftDeleted,
      },
      floorPlanAtMoveOut: {
        value: result?.floorPlanName ?? null,
      },
      moveInDate: {
        variant: 'date',
        value: result?.moveInDate,
      },
      moveOutDate: {
        variant: 'date',
        value: result?.moveOutDate,
      },
      FASDate: {
        variant: 'date',
        value: result?.FASDate,
      },
      monthsLateFeesCharges: {
        value: result?.monthsLateFeesCharges,
        variant: 'number',
      },
      underEviction: {
        variant: 'boolean',
        value: result?.underEviction,
        iconName: 'AlertInfoIcon',
        color: 'error',
      },
      doNotRenew: {
        variant: 'boolean',
        value: result?.doNotRenew,
        iconName: 'AlertInfoIcon',
        color: 'error',
      },
      collectionStatus: {
        variant: 'string',
        value: result?.collectionStatus?.split('_').map(_.capitalize).join(' '),
      },
      claimCreated: {
        variant: 'date',
        value: result?.claimCreated,
      },
      residentBalance: {
        variant: 'currency',
        value: result?.balance,
      },
      subsidyBalance: {
        variant: 'currency',
        value: result?.subsidyBalance,
      },
    }));
  }, [results, isCommercial]);
};

const useCSVExport = ({ hasAnyFilters, filteredHeaders, rows, enabled }) => {
  const handleCSVButtonClick = useCallback(() => {
    const csvRows = rows.map((row) => {
      const {
        texting: {
          value: { props: textingProps },
        },
      } = row;
      return {
        ...row,
        texting: {
          variant: 'string',
          value: `New = ${textingProps?.NEW ?? 0}, Unresolved = ${
            textingProps?.UNRESOLVED ?? 0
          }`,
        },
        underEviction: {
          ...row.underEviction,
          variant: 'string',
          value: row.underEviction.value ? 'Yes' : '',
        },
        doNotRenew: {
          ...row.doNotRenew,
          variant: 'string',
          value: row.doNotRenew.value ? 'Yes' : '',
        },
        unit: {
          value: row.unit?.value,
        },
      };
    });

    exportAndDownloadCSV({
      excludedHeaders: [],
      filteredHeaders,
      fileName: `Prior Residents_${formatDateDB(new Date())}`,
      rows: csvRows,
      hasAnyFilters,
    });
  }, [hasAnyFilters, filteredHeaders, rows]);
  return { handleCSVButtonClick: enabled ? handleCSVButtonClick : undefined };
};
